import type { z } from "zod";
import { shipmentContactInputSchema } from "../shipment-contact.schema";
import type { TFunction } from "i18next";
import type { ParsedOrder } from "./bulk-ship-form";
import { shippingTextSchema } from "@/lib/shipping-text";

const fields = [
  "recipientName",
  "recipientAddress",
  "recipientUnit",
  "recipientCity",
] as const;
type ShippingTextField = (typeof fields)[number];

/** Check original imported text before a geocoder can replace any of it. */
export function validateBulkShippingText(
  row: Record<ShippingTextField, string>
): ShippingTextField[] {
  return fields.filter(
    (field) => !shippingTextSchema().safeParse(row[field]).success
  );
}

export function updateBulkShippingRow<K extends keyof ParsedOrder>(
  row: ParsedOrder,
  field: K,
  value: ParsedOrder[K],
  t: TFunction
): ParsedOrder {
  const updated = { ...row, [field]: value };
  if (
    row[field] === value ||
    ![...fields, "recipientProvince", "recipientPostalCode"].some(
      (key) => key === field
    )
  )
    return updated;
  return {
    ...updated,
    status: "needs_fix",
    selectedQuote: undefined,
    quoteId: undefined,
    availableQuotes: undefined,
    validatedAddress: undefined,
    errorMessage:
      bulkShippingTextMessage(validateBulkShippingText(updated), t) ??
      t("validation.shippingTextRecheck"),
  };
}

const fieldLabels = {
  recipientName: "bulkShip.name",
  recipientAddress: "bulkShip.streetAddress",
  recipientUnit: "bulkShip.unit",
  recipientCity: "bulkShip.city",
} as const;

export function bulkShippingTextMessage(
  invalidFields: ShippingTextField[],
  t: TFunction
): string | undefined {
  if (invalidFields.length === 0) return undefined;
  return `${invalidFields.map((field) => t(fieldLabels[field])).join(", ")}: ${t("validation.shippingText")}`;
}

export function mergeBulkShippingRow(
  current: ParsedOrder[],
  source: ParsedOrder,
  result: ParsedOrder
): ParsedOrder[] {
  const inputFields = [
    "recipientName",
    "recipientAddress",
    "recipientUnit",
    "recipientCity",
    "recipientProvince",
    "recipientPostalCode",
    "recipientPhone",
    "recipientEmail",
    "weight",
    "weightUnit",
    "length",
    "width",
    "height",
    "dimensionUnit",
  ] as const;
  return current.map((row) => {
    if (
      row.id !== source.id ||
      row.id !== result.id ||
      row.status === "processing" ||
      row.status === "completed" ||
      inputFields.some((field) => row[field] !== source[field])
    )
      return row;
    return { ...result, selected: row.selected };
  });
}

export function bulkShippingContactMessage(
  row: Pick<
    ParsedOrder,
    "recipientName" | "recipientPhone" | "recipientEmail" | "validatedAddress"
  >,
  t: TFunction
): string | undefined {
  const contact = shipmentContactInputSchema(
    row.validatedAddress?.country ?? "CA",
    `${t("validation.phoneRequired")}. ${t("validation.phoneInvalid")}`
  ).safeParse({
    name: row.recipientName,
    phone: row.recipientPhone,
    email: row.recipientEmail,
  });
  if (contact.success) return undefined;
  return bulkShippingContactError(
    contact.error.issues,
    row.recipientName,
    "recipient",
    t
  );
}

export function bulkShippingContactError(
  issues: z.ZodIssue[],
  name: string,
  party: "sender" | "recipient",
  t: TFunction
): string {
  return issues

    .map((issue) => {
      const field = issue.path[0];
      const label = t(
        field === "phone"
          ? "bulkShip.phone"
          : field === "email"
            ? "bulkShip.email"
            : "bulkShip.name"
      );
      const detail =
        field === "phone"
          ? issue.message
          : t(
              field === "email"
                ? "createShipment.invalidEmail"
                : name.trim()
                  ? "validation.shippingText"
                  : party === "sender"
                    ? "createShipment.senderNameRequired"
                    : "createShipment.recipientNameRequired"
            );
      return `${label}: ${detail}`;
    })
    .join("; ");
}
